
import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw, CheckCircle, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/contexts/AuthContext';
import { useMeta } from '@/contexts/MetaContext';
import { exchangeCodeForToken } from '@/services/metaApi/auth';
import { toast } from 'sonner';

const MetaAuth: React.FC = () => {
  const { user } = useAuth();
  const { isConnected, isConnecting, setIsConnecting, accessToken, setConnected, setAccessToken } = useMeta();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [retryCount, setRetryCount] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>({});

  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const error = searchParams.get('error');
  const error_description = searchParams.get('error_description');

  // Add debug info collection
  useEffect(() => {
    const currentDebugInfo = {
      url: window.location.href,
      code: code ? code.substring(0, 10) + '...' : null,
      state,
      error,
      error_description,
      status,
      isConnected,
      isConnecting,
      hasToken: !!accessToken,
      timestamp: new Date().toISOString()
    };
    
    setDebugInfo(currentDebugInfo);
    console.log("MetaAuth Debug Info:", currentDebugInfo);
  }, [code, state, error, error_description, status, isConnected, isConnecting, accessToken]);

  // Process OAuth redirect when code is present
  useEffect(() => {
    if (!user) return;
    if (!code || !state) return;
    if (error) return;
    if (status !== 'idle') return;

    const processCode = async () => {
      try {
        console.log("Initiating code exchange process");
        setIsLoading(true);
        setStatus('loading');
        setIsConnecting(true);
        
        // Verify the state parameter
        const storedState = localStorage.getItem('meta_auth_state');
        if (state !== storedState) {
          console.error("State mismatch, potential CSRF attack");
          throw new Error("Sicherheitsfehler: State-Parameter stimmt nicht überein.");
        }
        
        // Exchange the code for a token
        const token = await exchangeCodeForToken(code);
        console.log("Code exchange completed, token received:", !!token);
        
        if (token) {
          setAccessToken(token);
          setConnected(true);
          setStatus('success');
          toast.success("Meta-Konto erfolgreich verbunden");
          
          // Clear the URL params to prevent reusing the code
          navigate('/meta-auth', { replace: true });
          
          // Redirect after a brief delay to show success message
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } else {
          throw new Error("Kein Token erhalten.");
        }
      } catch (error) {
        console.error("Error during code exchange:", error);
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Unbekannter Fehler');
        toast.error("Fehler bei der Meta-Verbindung: " + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
      } finally {
        setIsLoading(false);
        setIsConnecting(false);
      }
    };
    
    processCode();
  }, [user, code, state, error, navigate, setAccessToken, setConnected, setIsConnecting, status]);

  // If no user, redirect to login
  if (!user) {
    console.log("No user, redirecting to login");
    return <Navigate to="/login" />;
  }

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setStatus('idle'); // Reset status to trigger the exchange code process again
  };

  const renderErrorState = () => {
    // OAuth error from Meta
    if (error) {
      return (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Meta API Fehler</AlertTitle>
          <AlertDescription>
            <p>{error_description || 'Ein Fehler ist bei der Authentifizierung mit Meta aufgetreten.'}</p>
          </AlertDescription>
        </Alert>
      );
    }

    // General connection error
    if (status === 'error') {
      return (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Verbindungsfehler</AlertTitle>
          <AlertDescription>
            <p className="mb-3">{errorMessage || 'Ein Fehler ist bei der Verbindung mit der Meta API aufgetreten.'}</p>
            <Button variant="outline" onClick={handleRetry}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Erneut versuchen
            </Button>
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-marble-background">
      <div className="w-full max-w-md">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-center text-2xl">Meta API Verbindung</CardTitle>
            <CardDescription className="text-center">
              {status === 'success' 
                ? 'Verbindung erfolgreich hergestellt!'
                : 'Verbindung wird hergestellt...'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {renderErrorState()}

            <div className="flex flex-col items-center justify-center p-4">
              {status === 'loading' || isLoading ? (
                <div className="flex flex-col items-center gap-4">
                  <Spinner size="lg" />
                  <p className="text-hossTeal">Authentifizierung läuft...</p>
                </div>
              ) : status === 'success' ? (
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-lg">Meta API wurde erfolgreich verbunden</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Ihre Werbekonto-Daten werden jetzt synchronisiert
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 text-center">
                  <Loader2 className="h-8 w-8 text-hossTeal animate-spin" />
                  <p className="text-sm text-gray-500">
                    Warte auf Verbindungsbestätigung...
                  </p>
                  
                  {/* Debug information section */}
                  <div className="mt-4 w-full p-2 border border-gray-200 rounded-md bg-gray-50">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Debug Information:</p>
                    <div className="text-xs font-mono text-gray-600 overflow-auto max-h-24">
                      <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-center">
            {status === 'success' ? (
              <Button onClick={() => navigate('/')}>
                Zum Dashboard
              </Button>
            ) : status === 'error' ? (
              <Button variant="outline" onClick={() => navigate('/')}>
                Zurück zum Dashboard
              </Button>
            ) : null}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default MetaAuth;
